import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import Stepper from "../../../components/stepper/Stepper";
import StepFlowLayout from "../../../components/step-flow/StepFlowLayout";
import type { StepFlowAction } from "../../../components/step-flow/stepFlowActions";
import {
  StepComplete,
  StepConfirmItem,
  StepFormDescription,
  StepFormGroup,
  StepFormLabel,
  StepFormTitle,
} from "../../../components/step-flow/StepFlowFormParts";
import { completeTransition, stepTransition } from "../../../components/step-flow/stepFlowTransitions";
import Input from "../../../components/form-item/Input";
import Textarea from "../../../components/form-item/Textarea";
import Toggle from "../../../components/form-item/Toggle";
import Tab from "../../../components/tab/Tab";
import { HOSTNAME } from "../../../constant/host";

import MgkPlanCard from "./MgkPlanCard";
import {
  MGK_CONTACT_CATEGORY,
  MGK_PLANS,
  MgkPlanId,
  formatMgkPrice,
} from "../mgkContent";

// ===== 型 =====

type ApplyStep = "plan" | "info" | "confirm" | "complete";

type ContactMethod = "メール" | "電話";

interface ApplyFormData {
  plan: MgkPlanId | null;
  name: string;
  email: string;
  emailConfirm: string;
  position: string;
  company: string;
  tel: string;
  contactMethod: ContactMethod;
  message: string;
  agree: boolean;
}

const STEPS: ApplyStep[] = ["plan", "info", "confirm", "complete"];
const STEP_LABELS = ["プラン", "お客様情報", "確認", "完了"];
const CONTACT_METHODS: ContactMethod[] = ["メール", "電話"];

const INITIAL_FORM: ApplyFormData = {
  plan: null,
  name: "",
  email: "",
  emailConfirm: "",
  position: "",
  company: "",
  tel: "",
  contactMethod: "メール",
  message: "",
  agree: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

// ===== Component =====

interface MgkApplyFlowProps {
  /** LP に戻る */
  onClose: () => void;
}

/**
 * MGK のお申し込み / ご相談フロー（プラン → お客様情報 → 確認 → 完了）。
 * 送信は /contact と同じ contact API を使い、プラン・ポジション・希望連絡方法は
 * 本文（content）に畳み込む（管理者宛メールは既知フィールドしか表示しないため）。
 */
const MgkApplyFlow: React.FC<MgkApplyFlowProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<ApplyStep>("plan");
  const [form, setForm] = useState<ApplyFormData>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const stepIndex = STEPS.indexOf(step);
  const selectedPlan = useMemo(() => MGK_PLANS.find((p) => p.id === form.plan), [form.plan]);

  const update = useCallback(
    <K extends keyof ApplyFormData>(key: K, value: ApplyFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const goTo = useCallback((next: ApplyStep) => {
    setStep(next);
  }, []);

  const emailMismatch = form.emailConfirm !== "" && form.email !== form.emailConfirm;

  const isInfoValid =
    form.name.trim() !== "" &&
    EMAIL_RE.test(form.email) &&
    form.email === form.emailConfirm &&
    form.agree;

  const buildContent = useCallback(() => {
    const lines = [
      `【ご希望プラン】${selectedPlan ? `${selectedPlan.name}（${formatMgkPrice(selectedPlan)}）` : "未選択"}`,
      `【ポジション】${form.position || "未入力"}`,
      `【ご希望の連絡方法】${form.contactMethod}`,
      "",
      "【メッセージ】",
      form.message || "（なし）",
    ];
    return lines.join("\n");
  }, [form.contactMethod, form.message, form.position, selectedPlan]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body = new FormData();
      body.append("category", MGK_CONTACT_CATEGORY);
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("tel", form.tel);
      body.append("company", form.company);
      body.append("content", buildContent());
      body.append("agree", form.agree ? "同意する" : "同意しない");

      const response = await fetch(`${HOSTNAME}/api/contact/submit/`, {
        method: "POST",
        body,
      });
      const result = await response.json();
      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "送信に失敗しました");
      }
      goTo("complete");
    } catch (error) {
      console.error("MGK 送信エラー:", error);
      setSubmitError(
        error instanceof Error ? error.message : "送信に失敗しました。もう一度お試しください。"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [buildContent, form, goTo]);

  // ===== 各ステップ =====

  const renderPlanStep = () => (
    <motion.div key="plan" {...stepTransition}>
      <StepFormTitle>プランを選択</StepFormTitle>
      <StepFormDescription>
        ご利用規模に合わせてお選びください。PRO プランや追加オプションは個別にお見積りします。
      </StepFormDescription>
      <PlanGrid role="radiogroup" aria-label="サービスプラン">
        {MGK_PLANS.map((plan) => (
          <MgkPlanCard
            key={plan.id}
            plan={plan}
            selected={form.plan === plan.id}
            onSelect={() => update("plan", plan.id)}
          />
        ))}
      </PlanGrid>
    </motion.div>
  );

  const renderInfoStep = () => (
    <motion.div key="info" {...stepTransition}>
      <StepFormTitle>お客様情報</StepFormTitle>
      <StepFormDescription>ご連絡先をご記入ください。担当者より折り返しご連絡いたします。</StepFormDescription>

      <StepFormGroup>
        <Input
          label="お名前"
          option="required"
          type="text"
          placeholder="岡部 健一"
          value={form.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("name", e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="メールアドレス"
          option="required"
          type="email"
          placeholder="kenichi.okabe@example.jp"
          value={form.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("email", e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="メールアドレス（確認）"
          option="required"
          type="email"
          placeholder="再入力"
          value={form.emailConfirm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            update("emailConfirm", e.target.value)
          }
        />
        {emailMismatch ? (
          <span className="bodytext-11 u-text-green u-pl4">メールアドレスが一致しません</span>
        ) : null}
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="ポジション"
          option="optional"
          type="text"
          placeholder="コンプライアンスマネージャー"
          value={form.position}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("position", e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="会社名"
          option="optional"
          type="text"
          placeholder="株式会社○○"
          value={form.company}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("company", e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Input
          label="電話番号（日中連絡がとれる番号）"
          option="optional"
          type="tel"
          placeholder="03-1234-5678"
          value={form.tel}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => update("tel", e.target.value)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <StepFormLabel>ご希望の連絡方法</StepFormLabel>
        <Tab
          type="tab"
          list={CONTACT_METHODS}
          activeTab={form.contactMethod}
          onTabChange={(label) => update("contactMethod", label as ContactMethod)}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Textarea
          label="メッセージ"
          option="optional"
          placeholder="こちらのサービスを活用して、著作権侵害のリスクを低減したいと考えています。詳しい説明資料をいただけますか？"
          value={form.message}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update("message", e.target.value)}
          rows={5}
        />
      </StepFormGroup>

      <StepFormGroup>
        <Toggle value={form.agree} onClick={() => update("agree", !form.agree)}>
          <span className="bodytext">
            <a
              href="/privacypolicy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--soremo-blue)", textDecoration: "none", fontSize: "inherit" }}
            >
              個人情報の取り扱い
            </a>
            について同意します
          </span>
        </Toggle>
      </StepFormGroup>
    </motion.div>
  );

  const renderConfirmStep = () => (
    <motion.div key="confirm" {...stepTransition}>
      <StepFormTitle>入力内容の確認</StepFormTitle>
      <StepFormDescription>以下の内容で送信します。よろしいですか？</StepFormDescription>

      <StepConfirmItem
        label="サービスプラン"
        value={
          selectedPlan
            ? `${selectedPlan.name} — ${selectedPlan.summary}（${formatMgkPrice(selectedPlan)}）`
            : undefined
        }
        fallback="未選択"
      />
      <StepConfirmItem label="お名前" value={form.name} />
      <StepConfirmItem label="メールアドレス" value={form.email} />
      <StepConfirmItem label="ポジション" value={form.position} />
      <StepConfirmItem label="会社名" value={form.company} />
      <StepConfirmItem label="電話番号" value={form.tel} />
      <StepConfirmItem label="ご希望の連絡方法" value={form.contactMethod} />
      <StepConfirmItem label="メッセージ" value={form.message} />

      <p className="bodytext-11 u-text-light-gray u-mt16">
        ※ SOREMO では、個人情報の保護に関する法令を遵守するとともに、プライバシーポリシーに基づき、お客様の個人情報を取り扱います。
      </p>
    </motion.div>
  );

  const renderCompleteStep = () => (
    <motion.div key="complete" {...completeTransition}>
      <StepComplete title="ご相談を承りました">
        内容を確認の上、担当者よりご連絡いたします。
        <br />
        ご入力いただいたメールアドレスにも控えをお送りしましたのでご確認ください。
      </StepComplete>
    </motion.div>
  );

  const renderStep = () => {
    switch (step) {
      case "plan":
        return renderPlanStep();
      case "info":
        return renderInfoStep();
      case "confirm":
        return renderConfirmStep();
      case "complete":
        return renderCompleteStep();
      default:
        return null;
    }
  };

  // ===== フッター =====

  const primaryAction: StepFlowAction | undefined = (() => {
    switch (step) {
      case "plan":
        return {
          label: "お客様情報へ",
          onClick: () => goTo("info"),
          disabled: form.plan === null,
          rightIcon: "chevron_right",
        };
      case "info":
        return {
          label: "確認画面へ",
          onClick: () => goTo("confirm"),
          disabled: !isInfoValid,
          rightIcon: "chevron_right",
        };
      case "confirm":
        return {
          label: "送信",
          onClick: handleSubmit,
          loading: isSubmitting,
          rightIcon: "chevron_right",
        };
      case "complete":
        return {
          label: "サービス TOP へ",
          onClick: onClose,
          rightIcon: "chevron_right",
        };
      default:
        return undefined;
    }
  })();

  const secondaryAction: StepFlowAction | undefined = (() => {
    switch (step) {
      case "info":
        return { label: "戻る", onClick: () => goTo("plan"), leftIcon: "chevron_left" };
      case "confirm":
        return {
          label: "戻る",
          onClick: () => goTo("info"),
          disabled: isSubmitting,
          leftIcon: "chevron_left",
        };
      default:
        return undefined;
    }
  })();

  const footerCenter =
    submitError ? (
      <span className="bodytext-13 u-text-green">{submitError}</span>
    ) : step === "plan" && selectedPlan ? (
      <span className="bodytext u-text-gray">{formatMgkPrice(selectedPlan)}</span>
    ) : undefined;

  return (
    <StepFlowLayout
      onBack={step === "complete" ? () => navigate("/") : onClose}
      ariaLabel="MUSIC GATE KEEPER お申し込み"
      stepper={
        <Stepper stepLabels={STEP_LABELS} current={stepIndex + 1} showCheckOnComplete={true} />
      }
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerCenter={footerCenter}
    >
      <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
    </StepFlowLayout>
  );
};

export default MgkApplyFlow;
